import { SystemTimezone } from '../types';

export const POPULAR_IANA_TIMEZONES = [
  { iana: 'Europe/Madrid', name: 'Madrid / Barcelona (Spain)', city: 'Madrid', country: 'Spain', flag: '🇪🇸' },
  { iana: 'Europe/Paris', name: 'Paris (France)', city: 'Paris', country: 'France', flag: '🇫🇷' },
  { iana: 'Europe/London', name: 'London (UK)', city: 'London', country: 'United Kingdom', flag: '🇬🇧' },
  { iana: 'Europe/Berlin', name: 'Berlin (Germany)', city: 'Berlin', country: 'Germany', flag: '🇩🇪' },
  { iana: 'Europe/Rome', name: 'Rome (Italy)', city: 'Rome', country: 'Italy', flag: '🇮🇹' },
  { iana: 'Europe/Lisbon', name: 'Lisbon (Portugal)', city: 'Lisbon', country: 'Portugal', flag: '🇵🇹' },
  { iana: 'America/New_York', name: 'New York (US - Eastern)', city: 'New York', country: 'United States', flag: '🇺🇸' },
  { iana: 'America/Chicago', name: 'Chicago (US - Central)', city: 'Chicago', country: 'United States', flag: '🇺🇸' },
  { iana: 'America/Denver', name: 'Denver (US - Mountain)', city: 'Denver', country: 'United States', flag: '🇺🇸' },
  { iana: 'America/Los_Angeles', name: 'San Francisco / Los Angeles (US - Pacific)', city: 'San Francisco', country: 'United States', flag: '🇺🇸' },
  { iana: 'America/Mexico_City', name: 'Mexico City (Mexico)', city: 'Mexico City', country: 'Mexico', flag: '🇲🇽' },
  { iana: 'America/Bogota', name: 'Bogota (Colombia)', city: 'Bogota', country: 'Colombia', flag: '🇨🇴' },
  { iana: 'America/Lima', name: 'Lima (Peru)', city: 'Lima', country: 'Peru', flag: '🇵🇪' },
  { iana: 'America/Santiago', name: 'Santiago (Chile)', city: 'Santiago', country: 'Chile', flag: '🇨🇱' },
  { iana: 'America/Argentina/Buenos_Aires', name: 'Buenos Aires (Argentina)', city: 'Buenos Aires', country: 'Argentina', flag: '🇦🇷' },
  { iana: 'America/Sao_Paulo', name: 'São Paulo (Brazil)', city: 'São Paulo', country: 'Brazil', flag: '🇧🇷' },
  { iana: 'Asia/Tokyo', name: 'Tokyo (Japan)', city: 'Tokyo', country: 'Japan', flag: '🇯🇵' },
  { iana: 'Asia/Seoul', name: 'Seoul (South Korea)', city: 'Seoul', country: 'South Korea', flag: '🇰🇷' },
  { iana: 'Asia/Singapore', name: 'Singapore', city: 'Singapore', country: 'Singapore', flag: '🇸🇬' },
  { iana: 'Asia/Hong_Kong', name: 'Hong Kong', city: 'Hong Kong', country: 'China', flag: '🇭🇰' },
  { iana: 'Asia/Shanghai', name: 'Shanghai / Beijing (China)', city: 'Shanghai', country: 'China', flag: '🇨🇳' },
  { iana: 'Asia/Dubai', name: 'Dubai (UAE)', city: 'Dubai', country: 'United Arab Emirates', flag: '🇦🇪' },
  { iana: 'Asia/Kolkata', name: 'New Delhi / Mumbai (India)', city: 'New Delhi', country: 'India', flag: '🇮🇳' },
  { iana: 'Australia/Sydney', name: 'Sydney (Australia)', city: 'Sydney', country: 'Australia', flag: '🇦🇺' },
  { iana: 'UTC', name: 'UTC (Coordinated Universal Time)', city: 'UTC', country: 'Global', flag: '🌐' },
];

export const DEFAULT_SYSTEM_TIMEZONES: SystemTimezone[] = [
  {
    id: 'tz_madrid',
    name: 'Headquarters (Madrid)',
    iana: 'Europe/Madrid',
    flag: '🇪🇸',
    city: 'Madrid',
    country: 'Spain',
    isDefault: true,
  },
  {
    id: 'tz_new_york',
    name: 'Branch Office (New York)',
    iana: 'America/New_York',
    flag: '🇺🇸',
    city: 'New York',
    country: 'United States',
  },
  {
    id: 'tz_san_francisco',
    name: 'Tech Hub (San Francisco)',
    iana: 'America/Los_Angeles',
    flag: '🇺🇸',
    city: 'San Francisco',
    country: 'United States',
  },
  {
    id: 'tz_tokyo',
    name: 'Asia Hub (Tokyo)',
    iana: 'Asia/Tokyo',
    flag: '🇯🇵',
    city: 'Tokyo',
    country: 'Japan',
  },
  {
    id: 'tz_utc',
    name: 'UTC Servers & Logs',
    iana: 'UTC',
    flag: '🌐',
    city: 'UTC',
    country: 'Global',
  },
];

/**
 * Detects browser default timezone IANA string
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Madrid';
  } catch {
    return 'Europe/Madrid';
  }
}

/**
 * Format a Date or timestamp in a given IANA timezone as HH:mm:ss or HH:mm
 */
export function formatTimeInTimezone(
  date: Date | number = new Date(),
  timeZone: string = 'Europe/Madrid',
  includeSeconds: boolean = true
): string {
  try {
    const d = typeof date === 'number' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      second: includeSeconds ? '2-digit' : undefined,
      hour12: false,
    }).format(d);
  } catch (err) {
    // Fallback if invalid timezone string
    const d = typeof date === 'number' ? new Date(date) : date;
    return d.toTimeString().split(' ')[0];
  }
}

/**
 * Format full date and time string in a given IANA timezone
 */
export function formatDateTimeInTimezone(
  date: Date | number = new Date(),
  timeZone: string = 'Europe/Madrid'
): string {
  try {
    const d = typeof date === 'number' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    const d = typeof date === 'number' ? new Date(date) : date;
    return d.toLocaleString();
  }
}

/**
 * Get UTC offset display e.g. "UTC+2", "UTC-5", "UTC+0"
 */
export function getTimezoneOffsetDisplay(timeZone: string, date: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    if (tzPart && tzPart.value) {
      return tzPart.value.replace('GMT', 'UTC');
    }
  } catch {
    // Fallback calculation
  }

  try {
    // Rough calculation using target date string
    const nowUtc = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
    const nowTz = new Date(date.toLocaleString('en-US', { timeZone }));
    const diffHours = (nowTz.getTime() - nowUtc.getTime()) / 3600000;
    const sign = diffHours >= 0 ? '+' : '-';
    const absHours = Math.abs(diffHours);
    const intHours = Math.floor(absHours);
    const minPart = Math.round((absHours - intHours) * 60);
    return `UTC${sign}${intHours}${minPart > 0 ? `:${minPart.toString().padStart(2, '0')}` : ''}`;
  } catch {
    return 'UTC';
  }
}

/**
 * Converts a HH:mm or HH:mm:ss time string from one timezone to another
 */
export function convertClockTimeBetweenTimezones(
  clockTime: string,
  fromIana: string,
  toIana: string,
  referenceDate: Date = new Date()
): { convertedClockTime: string; diffHours: number; dayShift: number } {
  try {
    const parts = clockTime.split(':').map((p) => parseInt(p, 10) || 0);
    const hours = parts[0] ?? 12;
    const minutes = parts[1] ?? 0;
    const seconds = parts[2] ?? 0;

    // Create a base date in year-month-day from referenceDate
    const year = referenceDate.getUTCFullYear();
    const month = referenceDate.getUTCMonth();
    const day = referenceDate.getUTCDate();

    // Construct local string in fromIana
    // Calculate approximate offset difference
    const nowFrom = new Date(referenceDate.toLocaleString('en-US', { timeZone: fromIana }));
    const nowTo = new Date(referenceDate.toLocaleString('en-US', { timeZone: toIana }));
    const diffMs = nowTo.getTime() - nowFrom.getTime();
    const diffHours = diffMs / 3600000;

    // Total seconds from start of day in fromIana
    const totalSecFrom = hours * 3600 + minutes * 60 + seconds;
    let totalSecTo = totalSecFrom + Math.round(diffMs / 1000);

    let dayShift = 0;
    while (totalSecTo < 0) {
      totalSecTo += 86400;
      dayShift -= 1;
    }
    while (totalSecTo >= 86400) {
      totalSecTo -= 86400;
      dayShift += 1;
    }

    const newH = Math.floor(totalSecTo / 3600);
    const newM = Math.floor((totalSecTo % 3600) / 60);
    const newS = totalSecTo % 60;

    const formatted = `${newH.toString().padStart(2, '0')}:${newM.toString().padStart(2, '0')}:${newS.toString().padStart(2, '0')}`;
    return {
      convertedClockTime: formatted,
      diffHours,
      dayShift,
    };
  } catch {
    return {
      convertedClockTime: clockTime,
      diffHours: 0,
      dayShift: 0,
    };
  }
}
